import { useEffect, useMemo, useRef, useState } from 'react';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { fitSelectionDocument, insertSelectionDocument } from '@peruselab/structure-editor/domain/selectionTemplate';
import { pageOf } from '@peruselab/structure-editor/domain/page';
import { pageRect } from '@peruselab/structure-editor/domain/pageExtent';
import {
  aromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { drawingDocumentToSvg } from '@peruselab/structure-editor/rendering/svgExport';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeKey } from '../../hooks/useEscapeKey';

/** A precommit transaction, shared by native reusable selections: only Place touches history. */
export function SelectionPlacementDialog({
  base,
  payload,
  onPlace,
  onCancel
}: {
  readonly base: DrawingDocument;
  readonly payload: DrawingDocument;
  readonly onPlace: (placed: ReturnType<typeof insertSelectionDocument>) => void;
  readonly onCancel: () => void;
}) {
  const page = pageRect(pageOf(base));
  const [fit, setFit] = useState(false);
  const [at, setAt] = useState({ x: page.x + page.width / 2, y: page.y + page.height / 2 });
  const dialog = useRef<HTMLDivElement>(null);
  const placeButton = useRef<HTMLButtonElement>(null);
  useFocusTrap(true, dialog);
  useEscapeKey(true, onCancel);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    placeButton.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  const placed = useMemo(
    () => insertSelectionDocument(base, fit ? fitSelectionDocument(payload, page.width, page.height) : payload, at),
    [base, payload, at, fit, page.width, page.height]
  );
  const doc = placed.document;
  const svg = drawingDocumentToSvg(
    doc,
    {
      bondOrders: drawnBondOrders(doc),
      aromaticBondIds: aromaticBondIds(doc),
      stereoBonds: bondStereoValues(doc),
      overriddenBondIds: new Set(Object.keys(doc.depiction.bondLengthOverrides))
    },
    '#222222',
    doc.depiction.coordinateSet,
    'template-placement-',
    {},
    [],
    [],
    { geometry: 'authored', pageBounds: page, background: '#ffffff' }
  );
  return (
    <div className="export-dialog-backdrop">
      <div
        className="save-drawing-dialog template-placement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-placement-title"
        ref={dialog}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onPlace(placed);
          }}
        >
          <h2 id="template-placement-title">Place template</h2>
          <p>Move the preview with the position controls, or tap the paper. Place adds it in one step.</p>
          <label>
            <input type="checkbox" checked={fit} onChange={(event) => setFit(event.target.checked)} />
            Fit to page (otherwise keep size)
          </label>
          <button
            type="button"
            className="template-placement-preview"
            aria-label="Move template on preview paper"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setAt({
                x: page.x + ((event.clientX - rect.left) / rect.width) * page.width,
                y: page.y + ((event.clientY - rect.top) / rect.height) * page.height
              });
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: svg }} />
          </button>
          <label>
            Horizontal position
            <input
              type="range"
              min={page.x}
              max={page.x + page.width}
              value={at.x}
              onChange={(event) => setAt({ ...at, x: Number(event.target.value) })}
            />
          </label>
          <label>
            Vertical position
            <input
              type="range"
              min={page.y}
              max={page.y + page.height}
              value={at.y}
              onChange={(event) => setAt({ ...at, y: Number(event.target.value) })}
            />
          </label>
          <div className="persistence-actions">
            <button type="button" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" ref={placeButton}>
              Place
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
